import { Box, Typography } from "@mui/material";
import {
  ChangeCircle,
  CheckCircle,
  PauseCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import {
  getPicklistValue,
  type PicklistEntity,
} from "../../../functions/picklistValue";
import { ProjectIndividual } from "../canonical";

interface ProjectDetailsDashTaskProgressProps {
  selectedProject: ProjectIndividual | undefined;
  picklist: PicklistEntity[] | undefined;
}

const iconStyles = { my: 1.25, ml: -0.5 };
const bigNumberStyles = { mb: 1 };

function getStatusTestId(picklistLabel: string) {
  return `task-progress-card-${picklistLabel.toLowerCase().replace(/\s+/g, "-")}`;
}

export const ProjectTaskStatus = [
  {
    picklistLabel: "New",
    displayLabel: "Not Started",
    Icon: RadioButtonUnchecked,
    iconSx: { opacity: 0.54 },
  },
  {
    picklistLabel: "At Risk",
    displayLabel: "In Progress",
    Icon: ChangeCircle,
    iconSx: { color: "primary.main" },
  },
  {
    picklistLabel: "Complete",
    displayLabel: "Complete",
    Icon: CheckCircle,
    iconSx: { color: "success.light" },
  },
  {
    picklistLabel: "On Hold",
    displayLabel: "On Hold",
    Icon: PauseCircle,
    iconSx: { color: "warning.light" },
  },
];

const ProjectDetailsDashTaskProgress: React.FC<
  ProjectDetailsDashTaskProgressProps
> = ({ selectedProject, picklist }) => {
  const allTasks = selectedProject?.tasks ?? [];

  const labelCounts = allTasks.reduce<Record<string, number>>((acc, t: any) => {
    const statusCode = t?.status;
    if (statusCode == null) return acc;
    const pv = getPicklistValue(
      picklist ?? [],
      "project",
      "status",
      statusCode,
    );
    const rawLabel = pv?.label;
    if (!rawLabel) return acc;
    acc[rawLabel] = (acc[rawLabel] || 0) + 1;
    return acc;
  }, {});

  return (
    <Box
      sx={{
        display: "grid",
        gap: 1,
        gridTemplateColumns: { xs: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" },
        width: "100%",
        flex: 1,
        alignItems: "stretch",
      }}
    >
      {ProjectTaskStatus.map(
        ({ picklistLabel, displayLabel, Icon, iconSx }) => (
          <Box
            key={picklistLabel}
            data-testid={getStatusTestId(picklistLabel)}
            sx={{
              backgroundColor: "table.header",
              p: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              minHeight: 0,
            }}
          >
            <Icon sx={{ ...iconStyles, ...iconSx }} />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
              flex={1}
              width="100%"
            >
              <Typography variant="h6" sx={bigNumberStyles}>
                {labelCounts[picklistLabel] || 0}
              </Typography>
            </Box>
            <Box
              sx={{
                minHeight: "2.8em",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "flex-end",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  width: "100%",
                  textAlign: "left",
                  display: "-webkit-box",
                  overflow: "hidden",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  lineHeight: 1.4,
                  maxHeight: "2.8em",
                }}
              >
                {displayLabel}
              </Typography>
            </Box>
          </Box>
        ),
      )}
    </Box>
  );
};

export default ProjectDetailsDashTaskProgress;
